import React, { useEffect, useState } from 'react';

import SuccessModalContent from '@editor/components/SuccessModalContent/SuccessModalContent';
import { SUBSCRIPTION_POP_UP_CLICK } from '@editor/global/analytics/events';
import { useRevampedCheckoutFlowVariant } from '@editor/global/growthbookUtils';
import { isMobile } from '@editor/hooks/useWindowSize';
import {
  paymentStoreSelector,
  usePaymentStore,
} from '@editor/stores/paymentStore';
import RemoteDataStatus from '@editor/types/RemoteDataStatus';
import analytics from '@repo/analytics';
import {
  subscriptionStoreSelector,
  useSubscriptionStore,
} from '@repo/subscription/client';

import {
  planSelectionStoreSelector,
  usePlanSelectionStore,
} from '../../../stores/planSelectionStore';
import { useUserStore, userStoreSelector } from '../../../stores/userStore';
import { PaymentError, usePaymentStatus } from '../../Payment';
import LoadingPlaceholder from '../LoadingPlaceholder/LoadingPlaceholder';
import { ModalContentWrapper } from '../styles';
import SubscriptionSuccessModal from '../SubscriptionSuccessModal/SubscriptionSuccessModal';
import { SubscriptionModalProps } from '../types';

import PlanPurchaseDetails from './PlanPurchaseDetails/PlanPurchaseDetails';
import PlanSelector from './PlanSelector/PlanSelector';
import { CheckoutFlowVariants } from './VariantFlows/types';
import VariantFlow from './VariantFlows/VariantFlow';

/**
 * This component mounts the subscription modal content and renders the new checkout flows or the legacy checkout flows.
 */
export const SubscriptionModalContent: React.FC<
  Omit<SubscriptionModalProps, 'isOpen'>
> = ({
  onSuccess,
  hideSuccessModal,
  returnUrlParams,
  returnUrlFailed,
  resetReturnUrlFailed,
  shouldShowOnlyOnePlan,
  onClose: onCloseSubscriptionModal,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [successModalIsOpen, setSuccessModalIsOpen] = useState(false);
  const [subscriptionFlowState, setSubscriptionFlowState] = useState<
    'selection' | 'payment' | 'successful'
  >('selection');

  const paymentStatus = usePaymentStatus();

  const user = useUserStore(userStoreSelector.user);

  const currentSubscription = useSubscriptionStore(
    subscriptionStoreSelector.subscription
  );
  const isInitialized = useSubscriptionStore(
    subscriptionStoreSelector.isInitialized
  );
  const disabled = usePlanSelectionStore(planSelectionStoreSelector.disabled);
  const { setSelectionDisabled } = usePlanSelectionStore(
    planSelectionStoreSelector.actions
  );
  const plan = usePlanSelectionStore(planSelectionStoreSelector.plan);
  const yearly = usePlanSelectionStore(planSelectionStoreSelector.yearly);

  const paymentActions = usePaymentStore(paymentStoreSelector.actions);

  const revampedCheckoutFlowVariant = useRevampedCheckoutFlowVariant();

  useEffect(() => {
    if (user && returnUrlFailed && !currentSubscription?.active) {
      paymentActions.setStatus(RemoteDataStatus.Error, null);
      resetReturnUrlFailed?.();
    }
  }, [
    returnUrlFailed,
    user,
    paymentActions,
    currentSubscription?.active,
    resetReturnUrlFailed,
  ]);

  useEffect(() => {
    if (isInitialized) {
      setIsLoading(false);
    }
  }, [isInitialized]);

  const handlePaymentInit = (): void => {
    setSelectionDisabled(true);
    setSubscriptionFlowState('payment');

    // track when the user proceeds to payment
    analytics.track(SUBSCRIPTION_POP_UP_CLICK, {
      selectedPlan: plan,
      duration: yearly ? 'yearly' : 'monthly',
    });
  };

  const handlePaymentSuccess = (): void => {
    if (isMobile()) {
      setSubscriptionFlowState('successful');
      return;
    }

    if (hideSuccessModal) {
      onSuccess?.();
    } else {
      setSuccessModalIsOpen(true);
    }
    setSubscriptionFlowState('selection');
    setSelectionDisabled(false);
  };

  const handleSuccessModalClose = (): void => {
    onSuccess?.();
    setSuccessModalIsOpen(false);
    // close the subscription modal when the success modal is closed
    onCloseSubscriptionModal?.();
  };

  if (paymentStatus.error) {
    return (
      <ModalContentWrapper>
        <PaymentError type="subscription" />
      </ModalContentWrapper>
    );
  }

  if (subscriptionFlowState === 'successful') {
    return (
      <SuccessModalContent
        onSuccess={() => {
          onCloseSubscriptionModal?.();
        }}
      />
    );
  }

  // Show the new checkout flows based on the feature flag and subscription flow chosen
  if (
    revampedCheckoutFlowVariant !== CheckoutFlowVariants.Off &&
    (subscriptionFlowState === 'selection' ||
      subscriptionFlowState === 'payment')
  ) {
    return (
      <ModalContentWrapper>
        {isLoading && <LoadingPlaceholder />}
        {!!currentSubscription && (
          <VariantFlow
            plan={plan}
            returnUrlParams={returnUrlParams}
            shouldShowOnlyOnePlan={shouldShowOnlyOnePlan}
            subscriptionFlowState={subscriptionFlowState}
            variant={revampedCheckoutFlowVariant}
            onPaymentInit={handlePaymentInit}
            onPaymentSuccess={handlePaymentSuccess}
          />
        )}
        {!hideSuccessModal && (
          <SubscriptionSuccessModal
            isOpen={successModalIsOpen}
            onClose={handleSuccessModalClose}
          />
        )}
      </ModalContentWrapper>
    );
  }

  // Defaults (legacy)
  return (
    <ModalContentWrapper>
      {isLoading && <LoadingPlaceholder />}
      {!!currentSubscription && (
        <>
          <PlanSelector disabled={disabled || paymentStatus.loading} />
          <PlanPurchaseDetails
            returnUrlParams={returnUrlParams}
            subscriptionFlowState={subscriptionFlowState}
            onPaymentInit={handlePaymentInit}
            onPaymentSuccess={handlePaymentSuccess}
          />
        </>
      )}
      {!hideSuccessModal && (
        <SubscriptionSuccessModal
          isOpen={successModalIsOpen}
          onClose={handleSuccessModalClose}
        />
      )}
    </ModalContentWrapper>
  );
};
